import { useCallback, useEffect, useRef } from "react";

interface UseDrawerResizeObserverProps {
    isMobile: boolean;
    onWidthChange: (width: number) => void;
    children?: React.ReactNode;
}

/**
 * useDrawerResizeObserver
 *
 * Custom hook to observe and report the width of a drawer element.
 * Uses ResizeObserver and MutationObserver to track size/content changes and
 * calls onWidthChange with the new width. No-ops on mobile.
 *
 * @param isMobile - Whether the layout is in mobile mode
 * @param onWidthChange - Callback to report the drawer width
 * @param children - Drawer children (used to trigger re-measure)
 * @returns { drawerRef } - Ref to attach to the drawer element
 */
export const useDrawerResizeObserver = ({
    isMobile,
    onWidthChange,
    children,
}: UseDrawerResizeObserverProps) => {
    const drawerRef = useRef<HTMLDivElement>(null);

    // Measure the width of the drawer.
    const measureAndUpdateWidth = useCallback(() => {
        if (drawerRef.current && !isMobile) {
            const rect = drawerRef.current.getBoundingClientRect();
            if (rect.width > 0) {
                onWidthChange(Math.round(rect.width));
            }
        }
    }, [isMobile, onWidthChange]);

    // Use ResizeObserver and MutationObserver to track size and content changes.
    useEffect(() => {
        if (drawerRef.current && !isMobile) {
            const element = drawerRef.current;

            // MutationObserver for content changes
            // allows detection of dynamic content changes
            const mutationObserver = new MutationObserver(() => {
                // Small delay to ensure animations complete
                setTimeout(measureAndUpdateWidth, 100);
            });

            mutationObserver.observe(element, {
                childList: true,
                subtree: true,
                attributes: true,
                attributeFilter: ["class", "style", "aria-expanded"],
            });

            // ResizeObserver for size changes
            const resizeObserver = new ResizeObserver(() => {
                measureAndUpdateWidth();
            });

            resizeObserver.observe(element);

            // Initial measurement with delay to ensure content is rendered
            setTimeout(measureAndUpdateWidth, 200);

            return () => {
                mutationObserver.disconnect();
                resizeObserver.disconnect();
            };
        }
    }, [measureAndUpdateWidth, isMobile]);

    // Re-measure when children change (drawer is expanded/collapsed)
    useEffect(() => {
        if (!isMobile) {
            // Delay to allow for expand/collapse animations
            setTimeout(measureAndUpdateWidth, 150);
        }
    }, [children, measureAndUpdateWidth, isMobile]);

    return {
        drawerRef,
    };
};
